<script setup lang="ts">
import type { Registration, Sleeve, SleevesPerReg } from '~/types'
import { ref, watch, computed } from 'vue'
import { useRoute, navigateTo } from '#app'
// import { storeToRefs } from 'pinia'
import { useTabSessionStore } from '~/stores/tabSession' // adjust if you import differently
import { staggerDelay } from '~/composables/useCountUpTicker'

const props = defineProps<{
  registrations: Registration[] | undefined
  sleeves: SleevesPerReg[] | undefined
}>()
type RegSelected = {
  id: string
  selected: boolean
  expanded: boolean
}
type SleeveSelected = {
  id: string
  selected: boolean
}
const route = useRoute()
const selectedRegExpanded = ref<RegSelected[]>([])
const selectedSleeve = ref<SleeveSelected[]>([])
const lastRegistrationIndex = ref(-1)
const isSleeveSelected = computed(() => route.path.startsWith('/sleeve'))
function buildSelectedExpandedRegs(regs: Registration[]): RegSelected[] {
  return regs.map(r => ({
    id: r.id,
    selected: false,
    expanded: false
  }))
}
function buildSelectedSleeves(sleeve: Sleeve[]): SleeveSelected[] {
  return sleeve.map(s => ({
    id: s.id,
    selected: false
  }))
}
watch(
  () => props.registrations,
  (regs) => {
    /**
     * When registrations prop changes:
     * - rebuild the local registration UI state
     * - reset lastRegistrationIndex
     */
    if (regs && regs.length) {
      selectedRegExpanded.value = buildSelectedExpandedRegs(regs)
      lastRegistrationIndex.value = -1
    } else {
      selectedRegExpanded.value = []
      lastRegistrationIndex.value = -1
    }
  },
  { immediate: true } // run immediately on component mount
)
watch(
  () => props.sleeves,
  (sleeve) => {
    /**
     * When sleeves prop changes:
     * - rebuild the local sleeve UI state
     * - reset lastRegistrationIndex
     *
     * NOTE:
     * buildSelectedSleeves expects Sleeve[]
     * so make sure your props.sleeves matches the structure you expect here
     */
    if (sleeve && sleeve.length) {
      selectedSleeve.value = buildSelectedSleeves(sleeve)
      lastRegistrationIndex.value = -1
    } else {
      selectedSleeve.value = []
      lastRegistrationIndex.value = -1
    }
  },
  { immediate: true }

)
/** Reset when navigating back to the household section */
watch(
  () => route.path,
  (path) => {
    /**
     * If user returns to /household,
     * clear registration and sleeve selections
     */
    if (path.startsWith('/household')) {
      deselectRegistrationsAndSleeves()
    }
  }
)

/**
 * Stores the currently expanded registration id
 * Used to know which sleeves belong to the open registration
 */
const currentExpandedRegId = ref<string | number | null>(null)

const tabSession = useTabSessionStore()
/** Helpers used by template */

/**
 * Check if registration at given index is selected
 */
const isThisRegSelected = (index: number) =>
  !!selectedRegExpanded.value[index]?.selected

/**
 * Check if registration at given index is expanded
 */
const isThisRegExpanded = (index: number) =>
  !!selectedRegExpanded.value[index]?.expanded

// FIX: Verify the current expanded registration matches the one where the sleeve was picked
// const isThisSleeveSelected = (sleeveIndex: number) => {
//   if (lastRegistrationIndex.value !== currentExpandedRegId.value) {
//     return false
//   }
//   return !!selectedSleeve.value[sleeveIndex]?.selected
// }

/**
 * Check if sleeve at given index is selected
 *
 * Current logic only checks local sleeve selection state.
 * It does NOT verify whether the selected sleeve belongs to the currently expanded registration.
 */
const isThisSleeveSelected = (sleeveIndex: number) => {
  return !!selectedSleeve.value[sleeveIndex]?.selected
}

/**
 * Event emitted to parent component
 * Used to close the sidebar when navigating
 */
const emit = defineEmits(['close-sidebar'])

// const selectRegistration = (index: number) => {
//   selectedRegExpanded.value.forEach((item, i) => {
//     item.selected = i === index
//     item.expanded = false
//
//     if (item.selected) {
//       const selectedReg = props.registrations![index]
//       tabSession.setRegistrationId(selectedReg.id)
//       tabSession.setRegistrationObj(selectedReg)
//     }
//   })
//
//   // Reset sleeve selection reference and navigate
//   lastRegistrationIndex.value = -1
//   // Tell the sidebar to close before navigating
//   emit('close-sidebar')
//   navigateTo('/registration/overview')
// }

/**
 * Select a registration directly
 *
 * What this does:
 * 1. Marks only the clicked registration as selected
 * 2. Collapses all registrations
 * 3. Saves selected registration in tabSession
 * 4. Clears sleeve-related state
 * 5. Navigates to /registration/overview
 */
const selectRegistration = (index: number) => {
  selectedRegExpanded.value.forEach((item, i) => {
    item.selected = i === index
    item.expanded = false
  })

  const selectedReg = props.registrations?.[index] ?? null

  if (selectedReg) {
    tabSession.setRegistrationId(String(selectedReg.id))
    tabSession.setRegistrationObj(selectedReg)
    tabSession.setSleeveId(null)
    tabSession.setSleeveObj(null)
    tabSession.setExpandedRegistrationId(null)
  }

  currentExpandedRegId.value = null
  lastRegistrationIndex.value = -1

  emit('close-sidebar')
  navigateTo('/registration/overview')
}

// const expandRegistration = (index: number) => {
//   selectedRegExpanded.value.forEach((item, i) => {
//     // Toggle clicked reg, collapse others
//     if (i === index) {
//       item.expanded = !item.expanded
//     } else {
//       item.expanded = false
//     }
//
//     if (item.expanded)
//       currentExpandedRegId.value = item.id
//   })
// }

/**
 * Expand or collapse a registration
 *
 * What this does:
 * - Toggles the clicked registration
 * - Collapses all other registrations
 * - Saves the expanded registration id into tabSession
 */
const expandRegistration = (index: number) => {
  selectedRegExpanded.value.forEach((item, i) => {
    if (i === index) {
      item.expanded = !item.expanded

      if (item.expanded) {
        currentExpandedRegId.value = item.id
        tabSession.setExpandedRegistrationId(String(item.id))
      } else {
        currentExpandedRegId.value = null
        tabSession.setExpandedRegistrationId(null)
      }
    } else {
      item.expanded = false
    }
  })
}

// const selectSleeve = (sleeveIndex: number) => {
//   // 1. Clear any top-level registration "selected" highlights
//   // This prevents multiple cards from having the white background
//   selectedRegExpanded.value.forEach((reg) => {
//     reg.selected = false
//   })
//
//   selectedSleeve.value.forEach((sleeve, i) => {
//     sleeve.selected = i === sleeveIndex
//
//     if (sleeve.selected) {
//       const sleeveObj = props.sleeves?.find(a => a.registrationId == currentExpandedRegId.value)?.sleeves[sleeveIndex]
//       tabSession.setSleeveObj(sleeveObj)
//       const regObj = props.registrations?.find(a => a.id == `${currentExpandedRegId.value}`)
//       tabSession.setRegistrationObj(regObj)
//     }
//   })
//
//   lastRegistrationIndex.value = currentExpandedRegId.value
//   // Tell the sidebar to close before navigating
//   emit('close-sidebar')
//   navigateTo('/sleeve/overview')
// }

/**
 * Select a sleeve under the currently expanded registration
 *
 * What this does:
 * 1. Removes top-level registration selected highlight
 * 2. Selects only the clicked sleeve
 * 3. Finds the sleeve object from props
 * 4. Finds the registration object from props
 * 5. Saves both to tabSession
 * 6. Navigates to /sleeve/overview
 */
const selectSleeve = (sleeveIndex: number) => {
  selectedRegExpanded.value.forEach((reg) => {
    reg.selected = false
  })

  selectedSleeve.value.forEach((sleeve, i) => {
    sleeve.selected = i === sleeveIndex
  })

  const sleeveObj =
    props.sleeves
      ?.find(a => String(a.registrationId) === String(currentExpandedRegId.value))
      ?.sleeves[sleeveIndex] ?? null

  const regObj =
    props.registrations?.find(a => String(a.id) === String(currentExpandedRegId.value)) ?? null

  if (regObj) {
    tabSession.setRegistrationId(String(regObj.id))
    tabSession.setRegistrationObj(regObj)
  }

  if (sleeveObj) {
    tabSession.setSleeveId(String(sleeveObj.id))
    tabSession.setSleeveObj(sleeveObj)
  }

  if (currentExpandedRegId.value != null) {
    tabSession.setExpandedRegistrationId(String(currentExpandedRegId.value))
  }

  /**
   * Convert expanded registration id to number if possible
   * Otherwise fallback to -1
   */
  lastRegistrationIndex.value = Number(currentExpandedRegId.value) || -1

  emit('close-sidebar')
  navigateTo('/sleeve/overview')
}

/**
 * Clear all selected/expanded UI state
 */
const deselectRegistrationsAndSleeves = () => {
  selectedRegExpanded.value.forEach((item) => {
    item.selected = false
    item.expanded = false
  })
  selectedSleeve.value.forEach((item) => {
    item.selected = false
  })
  lastRegistrationIndex.value = -1
}

/**
 * Return sleeves for the currently expanded registration.
 * If nothing is expanded, return empty array.
 */
const currentSleeves = computed<Sleeve[]>(() => {
  if (!currentExpandedRegId.value || !props.sleeves) return []

  const item = props.sleeves.find(a => String(a.registrationId) === String(currentExpandedRegId.value))
  return item ? item.sleeves : []
})

/**
 * Sleeves to render under a registration in the sidebar tree.
 *
 * Three states:
 *   - reg is expanded → all sleeves under that reg
 *   - reg is collapsed AND owns the currently-selected sleeve → just
 *     that one sleeve (so the visual lineage to the selected page
 *     isn't lost when the user hides sleeves while on /sleeve/*)
 *   - otherwise → no sleeves shown
 *
 * Per #82. The collapsed-but-selected path matches the highlight
 * behaviour above: when we're on a sleeve page the owning reg stays
 * highlighted via tabSession.registrationId, and the selected sleeve
 * stays visible underneath it.
 */
/**
 * Identity-based "is this sleeve the active one?" check.
 *
 * The older isThisSleeveSelected(i) lookup walked selectedSleeve[i],
 * which only populates from the EXPANDED reg's sleeve list. When the
 * user hides sleeves on the reg that owns the active sleeve, we still
 * render that single sleeve (see getDisplayedSleevesFor) but the
 * index-based array is empty, so the green border + spine elbow would
 * vanish. Comparing by sleeve.id against tabSession.sleeveId works in
 * both the expanded and collapsed-but-selected render paths.
 */
function isSleeveItemSelected(sleeve: Sleeve): boolean {
  return (
    isSleeveSelected.value
    && !!tabSession.sleeveId
    && String(sleeve.id) === String(tabSession.sleeveId)
  )
}

/**
 * Find the index of the active sleeve inside the list we're ACTUALLY
 * rendering for this reg. The spine math (straight line above the
 * selected row, L-bend at the selected row) keys off this index.
 */
function selectedSleeveIndexFor(reg: Registration): number {
  return getDisplayedSleevesFor(reg).findIndex(s => isSleeveItemSelected(s))
}

function getDisplayedSleevesFor(reg: Registration): Sleeve[] {
  if (!props.sleeves) return []
  const owned = props.sleeves.find(a => String(a.registrationId) === String(reg.id))?.sleeves ?? []
  if (!owned.length) return []

  const expanded = String(currentExpandedRegId.value) === String(reg.id)
  if (expanded) return owned

  // Collapsed but this reg owns the currently-selected sleeve →
  // render just that sleeve as a single-row child so the user can see
  // where they are in the tree without re-expanding.
  if (
    isSleeveSelected.value
    && String(tabSession.registrationId) === String(reg.id)
    && tabSession.sleeveId
  ) {
    const onlySelected = owned.find(s => String(s.id) === String(tabSession.sleeveId))
    return onlySelected ? [onlySelected] : []
  }

  return []
}

// Update the helper used in the "Show X Sleeves" text too
/**
 * Helper function:
 * given a registration id, return its sleeves
 */
function getCurrentSleeves(curRegId: string | number): Sleeve[] {
  const item = props.sleeves?.find(a => String(a.registrationId) === String(curRegId))
  return item?.sleeves || []
}

/**
 * Find index of currently selected sleeve inside currentSleeves
 * Returns:
 * - index if found
 * - -1 if no sleeve is selected
 */
const selectedSleeveIndex = computed(() =>
  currentSleeves.value?.findIndex((_, idx) => isThisSleeveSelected(idx))
)

// watch(
//   () => [tabSession.registration, tabSession.sleeve, tabSession.expandedRegistrationId],
//   ([registration, sleeve, expandedRegistrationId]) => {
//     console.log('saved registration', registration)
//     console.log('saved sleeve', sleeve)
//     console.log('saved expandedRegistrationId', expandedRegistrationId)
//   },
//   { immediate: true, deep: true }
// )

/**
 * Restore UI state from tabSession whenever:
 * - registrations change
 * - sleeves change
 * - expandedRegistrationId changes
 * - registrationId changes
 * - sleeveId changes
 *
 * This helps preserve sidebar state when navigating between pages.
 */
watch(
  () => [props.registrations, props.sleeves, tabSession.expandedRegistrationId, tabSession.registrationId, tabSession.sleeveId],
  () => {
    if (!props.registrations?.length) return

    // Restore registrations state
    selectedRegExpanded.value = props.registrations.map((reg) => ({
      id: String(reg.id),

      /**
       * Registration is selected only if:
       * - its id matches tabSession.registrationId
       * - and there is no selected sleeve
       */
      selected: String(tabSession.registrationId) === String(reg.id) && !tabSession.sleeveId,

      /**
       * Registration is expanded if its id matches expandedRegistrationId
       */
      expanded: String(tabSession.expandedRegistrationId) === String(reg.id)
    }))

    currentExpandedRegId.value = tabSession.expandedRegistrationId
      ? String(tabSession.expandedRegistrationId)
      : null

    // Restore sleeves under the expanded registration
    const currentRegSleeves =
      props.sleeves?.find(
        a => String(a.registrationId) === String(tabSession.expandedRegistrationId)
      )?.sleeves ?? []

    selectedSleeve.value = currentRegSleeves.map((s) => ({
      id: String(s.id),
      selected: String(tabSession.sleeveId) === String(s.id)
    }))

    lastRegistrationIndex.value = tabSession.expandedRegistrationId
      ? Number(tabSession.expandedRegistrationId)
      : -1
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <div v-for="(reg, index) in props.registrations" :key="reg.id || index">
    <!--
      Highlight the reg when it's directly selected, OR when we're on a
      sleeve page and this reg owns the selected sleeve. The
      tabSession.registrationId comparison preserves the highlight when
      the user collapses the sleeve list ("Hide Sleeves") — the
      expanded-reg state goes null but the owning reg should keep its
      selected look as long as we're still on /sleeve/* (#66).
    -->
    <div
      class="w-full h-full rounded pl-0.5 my-3 "
      :class="{
        'bg-[#FFFFFF99] scale-105 hover:transition-transform duration-200 hover:border-[#284E36] hover:border-2 hover:scale-105 shadow-[0_4px_5px_rgba(0,0,0,0.28)]':
          isThisRegSelected(index)
          || (isSleeveSelected && String(currentExpandedRegId) === String(reg.id))
          || (isSleeveSelected && String(tabSession.registrationId) === String(reg.id)),
        'border-black/10 border-2 rounded my-3 hover:bg-white/60 hover:transition-transform duration-300 hover:scale-105 hover:border-[#284E36]':
          !isThisRegSelected(index)
          && !(isSleeveSelected && String(currentExpandedRegId) === String(reg.id))
          && !(isSleeveSelected && String(tabSession.registrationId) === String(reg.id))
      }"
    >
      <div class="hover:cursor-pointer py-2" @click="selectRegistration(index)">
        <div class="flex items-center justify-between pr-4">
          <h2 class="self-center pl-3 font-sans text-xs font-extrabold tracking-wider uppercase text-black truncate">
            {{ reg.name }}
          </h2>
        </div>
        <div class="flex items-center justify-between pr-4 mt-1">
          <h2 class="self-center pl-3 font-sans text-xs text-[#5A5A42]">
            {{ reg.accountNumber }}
          </h2>
          <!-- Market value counts up on first paint, matching the headline
               AUM figure on household/registration/sleeve. `index` walks the
               reveal down the column instead of flashing every account at
               once; AnimatedNumber snaps instead when motion is reduced. -->
          <p class="text-xs font-semibold text-black">
            <AnimatedNumber
              :value="reg.value"
              locale="en-US"
              :decimals="2"
              :delay="staggerDelay(index)"
              :duration="380"
              skeleton-width="w-16"
            />
          </p>
        </div>
      </div>

      <!-- Expand/Collapse row -->
      <div
        class="flex items-center pl-3 hover:cursor-pointer py-1 gap-2 select-none hover:bg-white/50"
        @click="expandRegistration(index)"
      >
        <UIcon
          name="i-lucide-chevron-down"
          class="size-5 transform transition-transform duration-300 ease-in-out"
          :class="{ 'rotate-180': isThisRegExpanded(index) }"
        />
        <p
          v-if="isThisRegExpanded(index)"
          class="uppercase text-[.6rem] underline text-gray-500 font-bold"
        >
          Hide Sleeves
        </p>
        <p v-else class="uppercase text-[.6rem] underline text-green-500 font-bold">
          Show {{ getCurrentSleeves(reg.id).length }} Sleeves
        </p>
      </div>
    </div>

    <!-- SLEEVES — see getDisplayedSleevesFor() for the three states
         (expanded / collapsed-but-selected / hidden) per #82. -->
    <div
      v-if="getDisplayedSleevesFor(reg).length > 0"
      :key="`expanded-${reg.id}`"
    >
      <div v-for="(sleeve, i) in getDisplayedSleevesFor(reg)" :key="sleeve.id" class="pl-8 relative">
        <!-- ─── L-LINE / SPINE LOGIC ───
             Indices are relative to getDisplayedSleevesFor(reg), which is
             either the full sleeve list (reg expanded) or a single-row
             list containing the active sleeve (reg collapsed but owns
             the active sleeve). selectedSleeveIndexFor() agrees with
             that — so in the collapsed case it returns 0 and the elbow
             renders correctly. -->
        <div
          v-if="selectedSleeveIndexFor(reg) !== -1 && i < selectedSleeveIndexFor(reg)"
          class="absolute left-0 top-0 -bottom-3 w-0.5 bg-[#284E36] scale-105"
        />
        <template v-if="selectedSleeveIndexFor(reg) !== -1 && i === selectedSleeveIndexFor(reg)">
          <div class="absolute left-0 top-0 h-[calc(50%+1px)] w-0.5 bg-[#284E36]" />
          <div class="absolute left-0 top-1/2 h-0.5 w-5 -translate-y-1/2 bg-[#284E36]" />
          <div class="absolute left-0 top-1/2 w-0.5 bg-[#284E36]" />
        </template>
        <!-- ─── EXISTING SLEEVE CARD ─── -->
        <div
          class="hover:cursor-pointer w-full h-full px-3 py-3 my-3 rounded hover:transition-transform duration-200 hover:scale-105 hover:shadow-[0_4px_5px_rgba(0,0,0,0.28)]"
          :class="{
            'bg-white border-l-4 border-[#284E36] hover:border-2':
              isSleeveItemSelected(sleeve),
            'border-black/10 border-2 hover:bg-white/60 hover:border-[#284E36]':
              !isSleeveItemSelected(sleeve)
          }"
          @click="selectSleeve(i)"
        >
          <div class="flex items-center justify-between">
            <h2 class="self-center font-sans text-xs font-extrabold tracking-wider uppercase text-black truncate">
              {{ sleeve.name }}
            </h2>
          </div>
          <div class="items-center justify-between mt-2">
            <p class="text-xs font-semibold text-black">
              <AnimatedNumber
                :value="Number(sleeve.value)"
                locale="en-US"
                :decimals="2"
                :delay="staggerDelay(i)"
                :duration="380"
                skeleton-width="w-16"
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* no-op */
</style>
